import { useState, useEffect } from 'react';
import { toast } from '../../../Toaster/toast';
import {
  PrismBanner,
  PrismLoading,
  PrismSwitch,
  PrismBadge,
  PrismInputText,
  PrismCheckbox,
} from '@prism-ui/react';
import {
  getRoster,
  toggleAutoRenew,
  removeOfficer,
  sendInvites,
  getInvites,
  cancelInvite,
  getActivity,
  RosterEntry,
  InviteEntry,
  ActivityEntry,
} from '../../../../services/officerManagement';
import { getAllOfficerRoles, OfficerRole } from '../../../../services/officerRoles';
import PermissionLevel from '../../../../data/PermissionLevel';
import { stripNameTitle } from '../../../../utils/nameUtils';
import { onCheckedChange } from '../../../../utils/prismCheckbox';
import './OfficerManagement.css';
import AdminPane from '../../AdminPane';

type Props = {
  currentUser: {
    Role: { permission_level: number };
    chapter_id?: number;
  };
};

type ActiveTab = 'roster' | 'activity';

const ALL_CHAPTERS_ID = 0;
const ACTIVITY_PAGE_SIZE = 10;

const OfficerManagement = ({ currentUser }: Props) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('roster');
  const [loading, setLoading] = useState(true);

  // Chapter filter
  const [chapters, setChapters] = useState<{ id: number; location: string }[]>([]);
  const [activeChapterId, setActiveChapterId] = useState<number | null>(null);

  // Roster state (all chapters, filtered client-side)
  const [allRoster, setAllRoster] = useState<RosterEntry[]>([]);

  // Invite state (all chapters, filtered client-side)
  const [allInvites, setAllInvites] = useState<InviteEntry[]>([]);
  const [officerRoles, setOfficerRoles] = useState<OfficerRole[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [inviteSending, setInviteSending] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteChapterId, setInviteChapterId] = useState<number | null>(null);

  // Activity state
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [activityLimit, setActivityLimit] = useState(ACTIVITY_PAGE_SIZE);

  // Roster sort
  type SortField = 'name' | 'email' | 'chapter' | 'roles' | 'status';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: SortField) =>
    sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // Remove officer modal
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<RosterEntry | null>(null);
  const [removeComment, setRemoveComment] = useState('');

  const isAdmin = currentUser.Role.permission_level >= PermissionLevel.Admin;
  const userChapterId = currentUser.chapter_id;
  const isAllView = activeChapterId === ALL_CHAPTERS_ID;
  const canModify = !isAllView && (isAdmin || activeChapterId === userChapterId);

  // Derived filtered data
  const rosterFiltered = isAllView
    ? allRoster
    : allRoster.filter(r => r.chapterId === activeChapterId);

  const roster = [...rosterFiltered].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'name':    cmp = a.name.localeCompare(b.name); break;
      case 'email':   cmp = a.email.localeCompare(b.email); break;
      case 'chapter': cmp = (a.chapter || '').localeCompare(b.chapter || ''); break;
      case 'roles':   cmp = (a.officerRoles.map(r => r.name).join(', ')).localeCompare(b.officerRoles.map(r => r.name).join(', ')); break;
      case 'status':  cmp = a.status.localeCompare(b.status); break;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      const [rosterData, inviteData, rolesData, chaptersRes, activityData] = await Promise.all([
        getRoster(),
        getInvites(),
        getAllOfficerRoles(),
        fetch('/api/chapters', { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        getActivity(),
      ]);
      setAllRoster(rosterData);
      setAllInvites(inviteData);
      setOfficerRoles(rolesData);
      setActivity(activityData);

      const allChapters = (chaptersRes as { id: number; location: string }[]).map(c => ({
        id: c.id,
        location: c.location,
      }));
      setChapters(allChapters);

      // Default to user's chapter on first load
      if (activeChapterId === null) {
        const defaultChapter = allChapters.find(c => c.id === userChapterId) || allChapters[0];
        if (defaultChapter) setActiveChapterId(defaultChapter.id);
      }
    } catch (err) {
      console.error('Failed to load officer management data:', err);
      toast.error('Failed to load officer management data');
    } finally {
      if (!isRefresh) setLoading(false);
    }
  };

  const handleToggleAutoRenew = async (entry: RosterEntry) => {
    try {
      await toggleAutoRenew(entry.id, !entry.autoRenew);
      setAllRoster(prev =>
        prev.map(r =>
          r.id === entry.id
            ? { ...r, autoRenew: !r.autoRenew, status: !r.autoRenew ? 'Active' : 'Rolling Off' }
            : r
        )
      );
      toast.success(`Auto-renew ${!entry.autoRenew ? 'enabled' : 'disabled'} for ${entry.name}`);
    } catch {
      toast.error('Failed to update auto-renew');
    }
  };

  const handleRemoveClick = (entry: RosterEntry) => {
    setRemoveTarget(entry);
    setRemoveComment('');
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget || !removeComment.trim()) return;
    try {
      await removeOfficer(removeTarget.id, removeComment);
      toast.success(`${removeTarget.name} has been removed as an officer`);
      setShowRemoveModal(false);
      setRemoveTarget(null);
      await loadData();
    } catch {
      toast.error('Failed to remove officer');
    }
  };

  const handleSendInvites = async () => {
    const emails = emailInput
      .split(/[,;\n]+/)
      .map(e => e.trim())
      .filter(Boolean);

    if (emails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }
    if (selectedRoleIds.length === 0) {
      toast.error('Please select at least one officer role');
      return;
    }

    const chapterId = isAdmin ? inviteChapterId : currentUser.chapter_id;
    if (!chapterId || chapterId === ALL_CHAPTERS_ID) {
      toast.error('Please select a chapter');
      return;
    }

    setInviteSending(true);
    try {
      const results = await sendInvites(emails, chapterId, selectedRoleIds);
      const sent = results.filter(r => r.status === 'invited').length;
      const errors = results.filter(r => r.status === 'error');
      const alreadyOfficer = results.filter(r => r.status === 'already_officer');

      const chapterName = chapters.find(c => c.id === chapterId)?.location || 'this chapter';
      if (sent > 0) toast.success(`${sent} invite(s) sent successfully`);
      if (alreadyOfficer.length > 0) {
        const names = alreadyOfficer.map(r => r.email).join(', ');
        toast.info(`Already an officer in ${chapterName}: ${names}`);
      }
      errors.forEach(e => toast.error(`${e.email}: ${e.error}`));

      if (sent > 0) {
        setEmailInput('');
        setSelectedRoleIds([]);
        setInviteChapterId(isAdmin ? null : currentUser.chapter_id ?? null);
        setShowInviteModal(false);
      }
      await loadData(true);
    } catch {
      toast.error('Failed to send invites');
    } finally {
      setInviteSending(false);
    }
  };

  const handleCancelInvite = async (id: number) => {
    try {
      await cancelInvite(id);
      toast.success('Invite cancelled');
      setAllInvites(prev => prev.map(i => (i.id === id ? { ...i, status: 'cancelled' } : i)));
    } catch {
      toast.error('Failed to cancel invite');
    }
  };


  const handleRoleToggle = (roleId: number) => {
    setSelectedRoleIds(prev =>
      prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  if (loading) {
    return <PrismLoading />;
  }

  return (
    <AdminPane as="div" width="wide" className="officer-management-container">
      <PrismBanner tone="info" layout="inline">
        {activeTab === 'roster' ? (
          <ul style={{ paddingLeft: '1.5rem', listStyleType: 'disc', marginBottom: 0 }}>
            <li>Manage the officer roster and control auto-renewal settings</li>
            <li><strong>Auto-Renew:</strong> when enabled, officer roles carry forward automatically on January 1st</li>
            <li><strong>Remove:</strong> downgrades the user to a regular role and removes all officer role assignments</li>
            <li>Individual officer role assignments can be managed on the <a href="/admin-settings/officer-roles">Officer Roles</a> page</li>
          </ul>
        ) : (
          <ul style={{ paddingLeft: '1.5rem', listStyleType: 'disc', marginBottom: 0 }}>
            <li><strong>Pending Invites:</strong> invites for unknown emails await the user's first SSO login to be accepted automatically</li>
            <li><strong>Recent Activity:</strong> tracks auto-renew changes filtered by the selected chapter</li>
            <li>Cancelling a pending invite is silent — no notification is sent to the invitee</li>
            <li>Use the <strong>+</strong> button to invite officers; chapter is selected inside the invite modal</li>
          </ul>
        )}
      </PrismBanner>

      <div className="officer-management-tabs">
        <button
          className={`officer-nav-btn${activeTab === 'roster' ? ' active' : ''}`}
          onClick={() => setActiveTab('roster')}
        >
          Officer Roster ({roster.length})
        </button>
        <button
          className={`officer-nav-btn${activeTab === 'activity' ? ' active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Activity{allInvites.filter(i => i.status === 'pending').length > 0
            ? ` (${allInvites.filter(i => i.status === 'pending').length})`
            : ''}
        </button>
        <button
          className="officer-add-btn"
          onClick={() => { setInviteChapterId(isAdmin ? null : (currentUser.chapter_id ?? null)); setShowInviteModal(true); }}
          title="Invite Officers"
        >
          +
        </button>
        <div className="officer-chapter-tabs">
          <button
            className={`officer-chapter-tab${isAllView ? ' active' : ''}`}
            onClick={() => { setActiveChapterId(ALL_CHAPTERS_ID); setSortField('chapter'); setSortDir('asc'); }}
          >
            All
          </button>
          {chapters.map(ch => (
            <button
              key={ch.id}
              className={`officer-chapter-tab${activeChapterId === ch.id ? ' active' : ''}${ch.id === userChapterId ? ' own-chapter' : ''}`}
              onClick={() => setActiveChapterId(ch.id)}
            >
              {ch.location}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'roster' && (
        <div className="officer-roster">
          {roster.length === 0 ? (
            <p>No officers found for {isAllView ? 'any chapter' : 'this chapter'} in the current year.</p>
          ) : (
            <table className="officer-table">
              <thead>
                <tr>
                  <th className="officer-sortable-th" onClick={() => toggleSort('name')}>
                    Name{sortIndicator('name')}
                  </th>
                  <th className="officer-sortable-th" onClick={() => toggleSort('email')}>
                    Email{sortIndicator('email')}
                  </th>
                  {isAllView && (
                    <th className="officer-sortable-th" onClick={() => toggleSort('chapter')}>
                      Chapter{sortIndicator('chapter')}
                    </th>
                  )}
                  <th className="officer-sortable-th" onClick={() => toggleSort('roles')}>
                    Officer Roles{sortIndicator('roles')}
                  </th>
                  <th className="officer-sortable-th" onClick={() => toggleSort('status')}>
                    Status{sortIndicator('status')}
                  </th>
                  {canModify && <th>Auto-Renew</th>}
                  {canModify && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {roster.map(entry => (
                  <tr key={entry.id}>
                    <td>{stripNameTitle(entry.name)}</td>
                    <td>{entry.email}</td>
                    {isAllView && <td>{entry.chapter}</td>}
                    <td>
                      {entry.officerRoles.map(r => (
                        <PrismBadge key={r.id} tone="info" style={{ marginRight: '4px' }}>
                          {r.name}
                        </PrismBadge>
                      ))}
                      {entry.officerRoles.length === 0 && <span className="officer-muted">None assigned</span>}
                    </td>
                    <td>
                      <PrismBadge tone={entry.autoRenew ? 'success' : 'caution'}>
                        {entry.status}
                      </PrismBadge>
                    </td>
                    {canModify && (
                      <td>
                        <PrismSwitch
                          label={`Auto-renew for ${stripNameTitle(entry.name)}`}
                          hideLabel={true}
                          checked={entry.autoRenew}
                          onPrismChange={onCheckedChange(entry.autoRenew, () => handleToggleAutoRenew(entry))}
                        />
                      </td>
                    )}
                    {canModify && (
                      <td>
                        <button
                          className="officer-btn-destructive"
                          onClick={() => handleRemoveClick(entry)}
                        >
                          Remove
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="officer-activity">
          {/* Pending Invites — filtered by chapter */}
          {(() => {
            const filteredInvites = isAllView
              ? allInvites
              : allInvites.filter(i => i.chapterId === activeChapterId);
            return (
              <>
                <h4 style={{ marginBottom: '0.5rem' }}>Pending Invites</h4>
                {filteredInvites.length === 0 ? (
                  <p className="officer-muted">No pending invites.</p>
                ) : (
                <table className="officer-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      {isAllView && <th>Chapter</th>}
                      <th>Invited By</th>
                      <th>Invited At</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvites.map(inv => (
                      <tr key={inv.id}>
                        <td>{inv.email}</td>
                        {isAllView && <td>{inv.chapter}</td>}
                        <td>{inv.invitedBy}</td>
                        <td>{new Date(inv.invitedAt).toLocaleDateString()}</td>
                        <td>
                          <PrismBadge
                            tone={
                              inv.status === 'pending' ? 'info'
                              : inv.status === 'accepted' ? 'success'
                              : inv.status === 'cancelled' ? 'caution'
                              : 'secondary'
                            }
                          >
                            {inv.status}
                          </PrismBadge>
                        </td>
                        <td>
                          {inv.status === 'pending' && (
                            <button
                              className="officer-btn-destructive"
                              onClick={() => handleCancelInvite(inv.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
              </>
            );
          })()}

          {/* Activity Timeline — filtered by chapter + paginated */}
          {(() => {
            const autoRenewEntries = activity
              .filter(e => e.column === 'auto_renew')
              .filter(e => isAllView || e.chapter_id === activeChapterId);
            const visible = autoRenewEntries.slice(0, activityLimit);
            const hasMore = autoRenewEntries.length > activityLimit;

            return (
              <>
                <h4 style={{ marginTop: '1.5rem', marginBottom: '0.5rem' }}>
                  Recent Activity
                  {autoRenewEntries.length > 0 && (
                    <span className="officer-muted" style={{ fontWeight: 'normal', fontSize: '0.8125rem' }}>
                      {' '}({autoRenewEntries.length})
                    </span>
                  )}
                </h4>
                {visible.length === 0 ? (
                  <p className="officer-muted">No auto-renew activity recorded yet.</p>
                ) : (
                  <>
                    <div className="officer-timeline">
                      {visible.map(entry => {
                        const target = entry.target_name ? stripNameTitle(entry.target_name) : 'Unknown';
                        return (
                          <div key={entry.id} className="officer-timeline-entry">
                            <div className="officer-timeline-dot" />
                            <div className="officer-timeline-content">
                              <span className="officer-timeline-desc">{entry.new_value} for {target}</span>
                              <span className="officer-timeline-meta">
                                {entry.action_by} — {new Date(entry.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {hasMore && (
                      <button
                        className="officer-btn-secondary officer-show-more"
                        onClick={() => setActivityLimit(prev => prev + ACTIVITY_PAGE_SIZE)}
                      >
                        Show more ({autoRenewEntries.length - activityLimit} remaining)
                      </button>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Invite Officers Modal */}
      {showInviteModal && (
        <div className="officer-modal-backdrop" onClick={() => setShowInviteModal(false)}>
          <div className="officer-modal officer-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="officer-modal-header">
              <strong>Invite Officers</strong>
              <button className="officer-modal-close" onClick={() => setShowInviteModal(false)}>✕</button>
            </div>
            <p className="officer-muted" style={{ margin: '0 0 0.75rem' }}>
              Enter the exact Comcast email — aliases are not supported. Existing users are promoted immediately.
            </p>
            <div className="invite-field">
              <label>Chapter</label>
              {isAdmin ? (
                <select
                  className="officer-select"
                  value={inviteChapterId ?? ''}
                  onChange={(e) => setInviteChapterId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">Select a chapter...</option>
                  {chapters.map(ch => (
                    <option key={ch.id} value={ch.id}>{ch.location}</option>
                  ))}
                </select>
              ) : (
                <span>{chapters.find(c => c.id === currentUser.chapter_id)?.location || 'Your chapter'}</span>
              )}
            </div>
            <div className="invite-field">
              <div className="invite-email-header">
                <PrismInputText
                  label="Email Address(es)"
                  value={emailInput}
                  placeholder="Enter email addresses separated by commas"
                  onPrismInput={(e: CustomEvent) => setEmailInput(e.detail.value)}
                />
                {emailInput.trim() && (
                  <button
                    type="button"
                    className="invite-email-clear"
                    onClick={() => setEmailInput('')}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="invite-field">
              <label>Officer Role(s)</label>
              <div className="officer-role-checkboxes">
                {[...officerRoles].sort((a, b) => a.name.localeCompare(b.name)).map(role => (
                  <PrismCheckbox
                    key={role.id}
                    name={`officer-role-${role.id}`}
                    label={role.name}
                    checked={selectedRoleIds.includes(role.id)}
                    onPrismChange={onCheckedChange(selectedRoleIds.includes(role.id), () => handleRoleToggle(role.id))}
                  />
                ))}
              </div>
            </div>
            <div className="officer-modal-actions">
              <button className="officer-btn-secondary" onClick={() => setShowInviteModal(false)}>
                Cancel
              </button>
              <button
                className="officer-btn-primary"
                onClick={handleSendInvites}
                disabled={inviteSending}
              >
                {inviteSending ? 'Sending...' : 'Send Invite(s)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Officer Modal */}
      {showRemoveModal && removeTarget && (
        <div className="officer-modal-backdrop" onClick={() => setShowRemoveModal(false)}>
          <div className="officer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="officer-modal-header">
              <strong>Remove {stripNameTitle(removeTarget.name)}</strong>
              <button className="officer-modal-close" onClick={() => setShowRemoveModal(false)}>✕</button>
            </div>
            <p style={{ margin: '0.5rem 0' }}>
              Are you sure? This will downgrade <strong>{stripNameTitle(removeTarget.name)}</strong> to a
              regular user and remove all officer role assignments.
            </p>
            <textarea
              className="officer-textarea"
              rows={2}
              value={removeComment}
              placeholder="Reason for removal (required)"
              onChange={(e) => setRemoveComment(e.target.value)}
            />
            <div className="officer-modal-actions">
              <button className="officer-btn-secondary" onClick={() => setShowRemoveModal(false)}>
                Cancel
              </button>
              <button
                className="officer-btn-destructive"
                onClick={handleConfirmRemove}
                disabled={!removeComment.trim()}
              >
                Remove Officer
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPane>
  );
};

export default OfficerManagement;
